import { z } from 'zod';

/**
 * Automations and cadences contract (docs/specs/011-automacoes-e-cadencias/spec.md):
 * the shapes the API answers, the zod of what it accepts and the limits both sides share.
 * The editor and the mocked API (`src/mocks/automations`) use these same definitions.
 */

export const AUTOMATION_KINDS = ['rule', 'cadence'] as const;
export type AutomationKind = (typeof AUTOMATION_KINDS)[number];

export const AUTOMATION_STATUSES = ['draft', 'active', 'paused'] as const;
export type AutomationStatus = (typeof AUTOMATION_STATUSES)[number];

export const TRIGGER_TYPES = [
  'customer.created',
  'deal.created',
  'deal.stage_changed',
  'deal.won',
  'deal.lost',
  'conversation.received',
  'conversation.no_reply',
  'task.overdue',
  'date.offset',
  'lead.received',
  /** No event starts it: an enrollment (from a record or a `start_cadence` step) does. */
  'manual',
  /** A WhatsApp group called the business (QR code only, spec 026). */
  'group.called',
  /** A WhatsApp group waits for an answer for some hours (QR code only, spec 026). */
  'group.waiting',
  /** Referral program (spec 028): each runs on the customer who referred. */
  'referral.received',
  'referral.converted',
  'referral.rewarded',
  /** The service cycle (spec 045): resolved, reopened, and an SLA target at risk or breached. */
  'conversation.resolved',
  'conversation.reopened',
  'sla.at_risk',
  'sla.breached',
  /** Spec 050: the customer became a lead, active or inactive (by hand or automatically). */
  'customer.situation_changed',
  /** The service case and the satisfaction score (spec 047). */
  'case.created',
  'case.resolved',
  /** Spec 056, D3: the ticket moved between its waits, and it passed its due date. */
  'case.status_changed',
  'case.overdue',
  'csat.received',
  /** Spec 050: an NPS answer, narrowed to promoters, passives or detractors. */
  'nps.received',
  /** Spec 071, R3: the orders of an integration. */
  'order.created',
  'order.closed',
  'order.cancelled',
  'order.open_for',
  /** Spec 072: the customer got one of the tags of the trigger. */
  'customer.tag_added',
] as const;
export type TriggerType = (typeof TRIGGER_TYPES)[number];

/** How a csat.received trigger reads its score filter (spec 047). */
export const CSAT_SCORE_MATCHES = ['at_most', 'exact'] as const;
export type CsatScoreMatch = (typeof CSAT_SCORE_MATCHES)[number];

export const STEP_TYPES = [
  'delay',
  'condition',
  'send_template',
  'create_task',
  'move_deal_stage',
  /** Opens a deal in another pipeline (the installation of a won sale). */
  'create_deal',
  /** Spec 068: looks for a deal of the person of the run; two paths, found and not found. */
  'find_deal',
  /** Spec 072: waits for a reply of the contact up to a deadline; two paths (replied, timed out). */
  'wait_for_reply',
  'assign',
  'add_tag',
  'remove_tag',
  'notify',
  'webhook',
  'send_message',
  'send_email',
  'update_field',
  'start_cadence',
  /** WhatsApp groups (QR code only, spec 026). */
  'create_group',
  'send_group_message',
  'close_group',
  /** The service cycle (spec 045): act on the conversation of the run. */
  'resolve_conversation',
  'transfer_conversation',
  'set_conversation_priority',
  /** The service case of the run record (spec 047). */
  'create_case',
  'set_case_reason',
  /** Spec 073: an action of a connected integration on the deal of the run ("Criar pedido no ERP"). */
  'integration_action',
] as const;
export type StepType = (typeof STEP_TYPES)[number];

/** Triggers and steps of WhatsApp groups: only a QR code connection has groups (spec 026). */
export const GROUP_TRIGGER_TYPES: readonly TriggerType[] = ['group.called', 'group.waiting'];
export const GROUP_STEP_TYPES: readonly StepType[] = [
  'create_group',
  'send_group_message',
  'close_group',
];

/** Steps that manage a group: saving them needs the permission to manage groups (rule 8). */
export const GROUP_MANAGE_STEP_TYPES: readonly StepType[] = ['create_group', 'close_group'];
export const GROUP_MANAGE_PERMISSION = 'core.whatsapp-group.manage';

/**
 * Steps that act on the run's conversation (spec 045): saving each needs the matching inbox
 * permission, like the group steps.
 */
export const CONVERSATION_STEP_TYPES: readonly StepType[] = [
  'resolve_conversation',
  'transfer_conversation',
  'set_conversation_priority',
  'create_case',
  'set_case_reason',
];
export const CONVERSATION_STEP_PERMISSIONS: Readonly<Partial<Record<StepType, string>>> = {
  resolve_conversation: 'core.conversation.resolve',
  transfer_conversation: 'core.conversation.transfer',
  set_conversation_priority: 'core.conversation.assign',
  create_case: 'core.case.create',
  set_case_reason: 'core.case.update',
};

/** The SLA target an `sla.*` trigger may be narrowed to (spec 045). */
/** The situations a customer.situation_changed trigger narrows to (spec 050). */
export const NPS_TRIGGER_CATEGORIES = ['promoter', 'passive', 'detractor'] as const;

export const TRIGGER_SITUATIONS = ['lead', 'active', 'inactive'] as const;

/** The statuses a `case.status_changed` trigger narrows to (spec 056, D3). */
export const TRIGGER_CASE_STATUSES = ['open', 'pending', 'on_hold'] as const;
export type TriggerSituation = (typeof TRIGGER_SITUATIONS)[number];

export const SLA_TRIGGER_TARGETS = ['first_response', 'next_response', 'resolution'] as const;
export type SlaTriggerTarget = (typeof SLA_TRIGGER_TARGETS)[number];

/** Where a `transfer_conversation` step sends the conversation. */
export const TRANSFER_TARGETS = ['queue', 'team', 'member'] as const;
export type TransferTarget = (typeof TRANSFER_TARGETS)[number];

/** The priority a `set_conversation_priority` step gives (the inbox's vocabulary). */
export const STEP_PRIORITIES = ['none', 'low', 'medium', 'high', 'urgent'] as const;
export type StepPriority = (typeof STEP_PRIORITIES)[number];

/** How the contacts get into a group an automation creates (spec 025, E6). */
export const GROUP_JOIN_MODES = ['invite', 'add'] as const;
export type GroupJoinMode = (typeof GROUP_JOIN_MODES)[number];

/** Steps with two paths (`nextStepIds = [yes, no]`, `''` = an empty path) — spec 068. */
export const BRANCH_STEP_TYPES: readonly StepType[] = ['condition', 'find_deal', 'wait_for_reply'];

export function isBranchStep(type: StepType): boolean {
  return BRANCH_STEP_TYPES.includes(type);
}

/** Which deals a `find_deal` step looks among: the open ones (default) or any. */
export const FIND_DEAL_STATUSES = ['open', 'any'] as const;
export type FindDealStatus = (typeof FIND_DEAL_STATUSES)[number];

/** The channels a `conversation.received` trigger may be narrowed to (spec 068). */
export const TRIGGER_CHANNEL_TYPES = ['whatsapp', 'instagram', 'email'] as const;
export type TriggerChannelType = (typeof TRIGGER_CHANNEL_TYPES)[number];

/**
 * Triggers a sweep starts (or nothing does): only the main trigger may be one of them, never
 * an extra trigger (spec 068).
 */
export const SWEEP_TRIGGER_TYPES: readonly TriggerType[] = [
  'manual',
  'conversation.no_reply',
  'date.offset',
  'group.waiting',
];

/** Who an `assign` step hands over to; `rotation` = the distribution of a queue (spec 068). */
export const ASSIGNEES = ['owner', 'member', 'rotation'] as const;
export type Assignee = (typeof ASSIGNEES)[number];

/** Steps a cadence may hold (linear nurture sequence). */
export const CADENCE_STEP_TYPES = [
  'delay',
  'send_template',
  'create_task',
  'send_message',
] as const;
export type CadenceStepType = (typeof CADENCE_STEP_TYPES)[number];

export const DELAY_UNITS = ['minutes', 'hours', 'days'] as const;
export type DelayUnit = (typeof DELAY_UNITS)[number];

export const CONDITION_FIELDS = [
  'customer.situation',
  'customer.tag',
  'deal.pipeline',
  'deal.stage',
  'channel',
  /** A custom field of the customer or the deal (`key` names the definition). */
  'customer.field',
  'deal.field',
  /** The value of the deal, in cents. */
  'deal.value',
  /** Spec 071, R7: the purchases of the customer, and their total in cents. */
  'customer.purchase_count',
  'customer.purchase_total',
  /** The unit of the deal, else the customer, else the conversation (its id). */
  'unit',
  /** `open` | `closed`: whether that unit is open now (hours and holidays). */
  'business_hours',
  /** `opted_in` | `opted_out` | `unknown`: the marketing consent of the contact. */
  'consent',
  /**
   * `open` | `closed`: whether the contact wrote on WhatsApp in the last 24 hours (the window in
   * which a reply is free and needs no template).
   */
  'whatsapp_window',
  /** Spec 072: the text of the reply a `wait_for_reply` step received. */
  'reply.text',
] as const;
export type ConditionField = (typeof CONDITION_FIELDS)[number];

export const CONDITION_OPERATORS = [
  'is',
  'is_not',
  'contains',
  'not_contains',
  'gt',
  'lt',
  'is_empty',
  'is_not_empty',
] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];

/** Operators that compare nothing: the value stays empty. */
export const VALUELESS_OPERATORS: readonly ConditionOperator[] = ['is_empty', 'is_not_empty'];

/** What each field accepts (a custom field's type narrows it further on screen). */
export const CONDITION_OPERATORS_BY_FIELD: Readonly<
  Record<ConditionField, readonly ConditionOperator[]>
> = {
  'customer.situation': ['is', 'is_not'],
  'customer.tag': ['is', 'is_not'],
  'deal.pipeline': ['is', 'is_not'],
  'deal.stage': ['is', 'is_not'],
  channel: ['is', 'is_not'],
  'customer.field': CONDITION_OPERATORS,
  'deal.field': CONDITION_OPERATORS,
  'deal.value': ['is', 'is_not', 'gt', 'lt'],
  'customer.purchase_count': ['is', 'gt', 'lt'],
  'customer.purchase_total': ['gt', 'lt'],
  unit: ['is', 'is_not'],
  business_hours: ['is'],
  consent: ['is', 'is_not'],
  whatsapp_window: ['is'],
  'reply.text': ['is', 'is_not', 'contains', 'not_contains', 'is_empty', 'is_not_empty'],
};

/** Fields whose condition names a custom field (`key`). */
export const CUSTOM_FIELD_CONDITIONS: readonly ConditionField[] = ['customer.field', 'deal.field'];

export const BUSINESS_HOURS_VALUES = ['open', 'closed'] as const;
export const CONSENT_VALUES = ['opted_in', 'opted_out', 'unknown'] as const;
export const WHATSAPP_WINDOW_VALUES = ['open', 'closed'] as const;

/** What an `update_field` step writes on. */
export const FIELD_TARGETS = ['customer', 'deal'] as const;
export type FieldTarget = (typeof FIELD_TARGETS)[number];

/** `running`/`waiting` (parked on a delay) are in progress; the rest are final. */
export const RUN_STATUSES = [
  'running',
  'waiting',
  'succeeded',
  'failed',
  'skipped',
  'stopped',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/** Spec 050: the customer's latest won deal and a date custom field of the customer. */
export const DATE_ANCHORS = [
  'birthday',
  'customer.created',
  'deal.created',
  'deal.won',
  'customer.last_purchase',
  'customer.field',
] as const;
export type DateAnchor = (typeof DATE_ANCHORS)[number];

/** Who a task or a notice goes to. */
export const PERSON_TARGETS = ['owner', 'member', 'creator'] as const;
export type PersonTarget = (typeof PERSON_TARGETS)[number];

/** What an assign step hands over. */
export const ASSIGN_TARGETS = ['auto', 'conversation', 'deal', 'customer'] as const;
export type AssignTarget = (typeof ASSIGN_TARGETS)[number];

export const TASK_REMINDERS = ['none', 'at-time', '15m', '1h', '1d'] as const;
export type TaskReminder = (typeof TASK_REMINDERS)[number];

export const AUTOMATION_LIMITS = {
  nameMin: 2,
  nameMax: 80,
  descriptionMax: 500,
  stepNameMax: 60,
  maxActive: 50,
  maxSteps: 30,
  maxConditions: 10,
  noReplyHoursMin: 1,
  noReplyHoursMax: 168,
  groupWaitingHoursMin: 1,
  groupWaitingHoursMax: 168,
  orderOpenDaysMin: 1,
  orderOpenDaysMax: 90,
  groupSubjectMax: 100,
  /** Groups a group trigger may be narrowed to (none = every monitored group). */
  groupTriggerGroupsMax: 50,
  groupDescriptionMax: 500,
  delayAmountMin: 1,
  delayAmountMax: 365,
  messageMax: 1000,
  emailSubjectMax: 200,
  emailBodyMax: 10000,
  frequencyDaysMin: 1,
  frequencyDaysMax: 365,
  listPageSize: 50,
  runsListed: 20,
  folderNameMax: 60,
  maxFolders: 100,
  /** Spec 068: triggers a rule may have besides the main one. */
  maxExtraTriggers: 4,
  triggerChannelConnectionsMax: 20,
  /** Spec 072: tags a `customer.tag_added` trigger may list. */
  triggerTagsMax: 10,
  /** Spec 072: the part of a reply a run keeps for its conditions. */
  replyTextMax: 1000,
} as const;

export const AUTOMATION_ERROR_CODES = [
  'AUTOMATION_NOT_FOUND',
  'AUTOMATION_INVALID',
  'AUTOMATION_NAME_TAKEN',
  'AUTOMATION_LIMIT',
  'AUTOMATION_ALREADY_ENROLLED',
  'AUTOMATION_RUN_NOT_FOUND',
  'AUTOMATION_GROUP_MANAGE_FORBIDDEN',
  'AUTOMATION_CONVERSATION_STEP_FORBIDDEN',
  'AUTOMATION_FOLDER_NOT_FOUND',
  'AUTOMATION_FOLDER_NAME_TAKEN',
  'AUTOMATION_FOLDER_LIMIT',
  'AUTOMATION_VERSION_NOT_FOUND',
] as const;

export interface PersonRef {
  id: string;
  name: string;
}

const idSchema = z.string().min(1);
const nameSchema = z.string().trim().min(AUTOMATION_LIMITS.nameMin).max(AUTOMATION_LIMITS.nameMax);

export const ConditionSchema = z
  .object({
    field: z.enum(CONDITION_FIELDS),
    operator: z.enum(CONDITION_OPERATORS),
    /** Empty while drafting and with `is_empty`/`is_not_empty` (activating checks the rest). */
    value: z.string().trim().max(120),
    /** The custom field's definition key (`customer.field`, `deal.field`). */
    key: z.string().trim().max(60).nullable().optional(),
  })
  .strict();
export type Condition = z.infer<typeof ConditionSchema>;

const GroupIdsSchema = z.array(idSchema).max(AUTOMATION_LIMITS.groupTriggerGroupsMax).optional();

const minTotalCents = z.number().int().min(0).nullable().optional();

export const TriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('customer.created') }).strict(),
  z
    .object({
      type: z.literal('deal.created'),
      pipelineId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('deal.stage_changed'),
      pipelineId: z.string().nullable().optional(),
      stageId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('deal.won'),
      pipelineId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('deal.lost'),
      pipelineId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('conversation.received'),
      /** Only the first message of a new contact. */
      firstContactOnly: z.boolean().optional(),
      /** Spec 068: only these channels and these numbers or accounts; none = any. */
      channelTypes: z.array(z.enum(TRIGGER_CHANNEL_TYPES)).max(3).optional(),
      channelConnectionIds: z
        .array(idSchema)
        .max(AUTOMATION_LIMITS.triggerChannelConnectionsMax)
        .optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('conversation.no_reply'),
      hours: z
        .number()
        .int()
        .min(AUTOMATION_LIMITS.noReplyHoursMin)
        .max(AUTOMATION_LIMITS.noReplyHoursMax),
    })
    .strict(),
  z.object({ type: z.literal('task.overdue') }).strict(),
  z
    .object({
      type: z.literal('date.offset'),
      /** Negative = days before the anchor (a renewal, a warranty). */
      offsetDays: z.number().int().min(-365).max(365),
      anchor: z.enum(DATE_ANCHORS),
      fieldKey: z.string().nullable().optional(),
    })
    .strict(),
  z.object({ type: z.literal('lead.received') }).strict(),
  z.object({ type: z.literal('manual') }).strict(),
  z
    .object({
      type: z.literal('group.called'),
      /** One QR code connection; none = any. */
      channelConnectionId: z.string().nullable().optional(),
      /** Only these monitored groups; none = every one (each group its own rule). */
      channelGroupIds: GroupIdsSchema,
      /** Only an alert word (not a mention nor a quote of the business). */
      byKeywordOnly: z.boolean().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('group.waiting'),
      channelGroupIds: GroupIdsSchema,
      hours: z
        .number()
        .int()
        .min(AUTOMATION_LIMITS.groupWaitingHoursMin)
        .max(AUTOMATION_LIMITS.groupWaitingHoursMax),
    })
    .strict(),
  z.object({ type: z.literal('referral.received') }).strict(),
  z
    .object({
      type: z.literal('referral.converted'),
      /** Only when the win earned the referrer a reward. */
      rewardedOnly: z.boolean().optional(),
    })
    .strict(),
  z.object({ type: z.literal('referral.rewarded') }).strict(),
  // Spec 045: the service cycle.
  z
    .object({
      type: z.literal('conversation.resolved'),
      queueId: z.string().nullable().optional(),
      channelConnectionId: z.string().nullable().optional(),
      outcomeId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({ type: z.literal('conversation.reopened'), queueId: z.string().nullable().optional() })
    .strict(),
  z
    .object({
      type: z.literal('sla.at_risk'),
      target: z.enum(SLA_TRIGGER_TARGETS).nullable().optional(),
      queueId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('sla.breached'),
      target: z.enum(SLA_TRIGGER_TARGETS).nullable().optional(),
      queueId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('customer.situation_changed'),
      to: z.enum(TRIGGER_SITUATIONS).nullable().optional(),
      autoOnly: z.boolean().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('nps.received'),
      category: z.enum(NPS_TRIGGER_CATEGORIES).nullable().optional(),
    })
    .strict(),
  // Spec 071, R3: the orders of an integration; `minTotalCents` narrows to the larger ones.
  z.object({ type: z.literal('order.created'), minTotalCents: minTotalCents }).strict(),
  z.object({ type: z.literal('order.closed'), minTotalCents: minTotalCents }).strict(),
  z.object({ type: z.literal('order.cancelled'), minTotalCents: minTotalCents }).strict(),
  z
    .object({
      type: z.literal('order.open_for'),
      days: z
        .number()
        .int()
        .min(AUTOMATION_LIMITS.orderOpenDaysMin)
        .max(AUTOMATION_LIMITS.orderOpenDaysMax),
      minTotalCents: minTotalCents,
    })
    .strict(),
  // Spec 072: any of these tags; a draft may have none, activating requires one.
  z
    .object({
      type: z.literal('customer.tag_added'),
      tags: z.array(z.string().trim().min(1).max(60)).max(AUTOMATION_LIMITS.triggerTagsMax),
    })
    .strict(),
  // Spec 047: the service case and the satisfaction score.
  z
    .object({
      type: z.literal('case.created'),
      queueId: z.string().nullable().optional(),
      reasonId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('case.status_changed'),
      to: z.enum(TRIGGER_CASE_STATUSES).nullable().optional(),
      queueId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('case.overdue'),
      queueId: z.string().nullable().optional(),
      reasonId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('case.resolved'),
      queueId: z.string().nullable().optional(),
      reasonId: z.string().nullable().optional(),
      outcomeId: z.string().nullable().optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal('csat.received'),
      score: z.number().int().min(1).max(5).nullable().optional(),
      scoreMatch: z.enum(CSAT_SCORE_MATCHES).optional(),
    })
    .strict(),
]);
export type Trigger = z.infer<typeof TriggerSchema>;

const delayConfig = z
  .object({
    amount: z
      .number()
      .int()
      .min(AUTOMATION_LIMITS.delayAmountMin)
      .max(AUTOMATION_LIMITS.delayAmountMax),
    unit: z.enum(DELAY_UNITS),
  })
  .strict();

const StepBase = {
  id: idSchema,
  name: z.string().trim().min(1).max(AUTOMATION_LIMITS.stepNameMax),
  /**
   * The next step (one) or, for a condition, `[then, else]` where `''` is an empty branch
   * (the run ends there).
   */
  nextStepIds: z.array(z.string()).max(2),
};

export const StepSchema = z.discriminatedUnion('type', [
  z.object({ ...StepBase, type: z.literal('delay'), config: delayConfig }).strict(),
  z.object({ ...StepBase, type: z.literal('wait_for_reply'), config: delayConfig }).strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('condition'),
      config: z
        .object({
          conditions: z.array(ConditionSchema).min(1).max(AUTOMATION_LIMITS.maxConditions),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('send_template'),
      config: z
        .object({
          /** The template's name on the channel (the language is picked when sending). */
          templateKey: z.string().trim().max(120),
          /**
           * One per `{{n}}` of the template; may use `{{customer.name}}`,
           * `{{customer.first_name}}`, `{{deal.title}}`, `{{customer.field.<key>}}`...
           */
          variables: z.array(z.string().trim().max(200)).max(10).optional(),
          /**
           * The WhatsApp number it goes out from; none = the conversation's number when it has
           * templates, else the first number of the unit with this template.
           */
          channelConnectionId: z.string().nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('create_task'),
      config: z
        .object({
          title: z.string().trim().min(1).max(120),
          /** Task body; may use {{customer.name}}, {{deal.title}}... */
          description: z.string().trim().max(1000).nullable().optional(),
          /** Activity type; null = the organization's first active type. */
          typeId: z.string().nullable().optional(),
          dueInDays: z.number().int().min(0).max(365),
          /** `HH:MM` in the organization's time zone; null = all day. */
          dueTime: z
            .string()
            .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
            .nullable()
            .optional(),
          reminder: z.enum(TASK_REMINDERS).optional(),
          priority: z.enum(['low', 'normal', 'high']).optional(),
          /** Who gets it: the record's owner (default), a member, or the automation's creator. */
          assignee: z.enum(PERSON_TARGETS).optional(),
          memberId: z.string().nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('move_deal_stage'),
      config: z
        .object({
          pipelineId: z.string(),
          stageId: z.string(),
          /** Required when the stage is a lost one. */
          lostReasonId: z.string().nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('create_deal'),
      config: z
        .object({
          pipelineId: z.string(),
          /** An open stage of the pipeline. */
          stageId: z.string(),
          /** May use {{customer.name}}, {{deal.title}}...; empty = the source deal's title. */
          title: z.string().trim().max(120).nullable().optional(),
          /** Who owns it: the record's owner (default), a member, or the automation's creator. */
          owner: z.enum(PERSON_TARGETS).optional(),
          memberId: z.string().nullable().optional(),
          /** Copies the source deal's items (when the pipeline takes items), else its value. */
          copyItems: z.boolean().optional(),
          /** Spec 068: the next steps act on the new deal instead of the source one. */
          continueWithDeal: z.boolean().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('find_deal'),
      config: z
        .object({
          /** Empty while drafting. */
          pipelineId: z.string(),
          /** Only in this stage; none = any stage of the pipeline. */
          stageId: z.string().nullable().optional(),
          status: z.enum(FIND_DEAL_STATUSES).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('assign'),
      config: z
        .object({
          assignee: z.enum(ASSIGNEES),
          memberId: z.string().nullable().optional(),
          /** The queue whose distribution picks the member (`rotation`). */
          queueId: z.string().nullable().optional(),
          /** What gets the owner; `auto` = the conversation, else the deal, else the customer. */
          target: z.enum(ASSIGN_TARGETS).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('add_tag'),
      config: z.object({ tag: z.string().trim().min(1).max(60) }).strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('remove_tag'),
      config: z.object({ tag: z.string().trim().min(1).max(60) }).strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('notify'),
      config: z
        .object({
          message: z.string().trim().min(1).max(280),
          recipient: z.enum(PERSON_TARGETS).optional(),
          memberId: z.string().nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('webhook'),
      config: z.object({ url: z.union([z.literal(''), z.string().url().max(500)]) }).strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('integration_action'),
      config: z
        .object({
          /** The integration (`citybox-erp`) and its action on a deal (`create_order`). */
          integrationId: z.string(),
          actionKey: z.string(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('send_message'),
      config: z
        .object({
          /** Free text inside the 24-hour window; may use `{{customer.name}}`... */
          text: z.string().trim().max(AUTOMATION_LIMITS.messageMax),
          /** The template sent instead when the window is closed (none = the step fails). */
          templateKey: z.string().trim().max(120).nullable().optional(),
          variables: z.array(z.string().trim().max(200)).max(10).optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('send_email'),
      config: z
        .object({
          /** The e-mail connection it goes from. */
          channelConnectionId: z.string(),
          /** Both may use `{{customer.name}}`... */
          subject: z.string().trim().max(AUTOMATION_LIMITS.emailSubjectMax),
          text: z.string().trim().max(AUTOMATION_LIMITS.emailBodyMax),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('update_field'),
      config: z
        .object({
          target: z.enum(FIELD_TARGETS),
          /** The custom field's definition key. */
          key: z.string().trim().max(60),
          /** Checked against the definition when it runs; null clears the field. */
          value: z.union([
            z.string().max(1000),
            z.number(),
            z.boolean(),
            z.array(z.string().max(120)).max(50),
            z.null(),
          ]),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('start_cadence'),
      /** An active cadence of the organization. */
      config: z.object({ automationId: z.string() }).strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('create_group'),
      config: z
        .object({
          /** A QR code connection with groups on; empty while drafting. */
          channelConnectionId: z.string(),
          /** May use `{{customer.name}}`, `{{deal.title}}`. */
          subject: z.string().trim().max(AUTOMATION_LIMITS.groupSubjectMax),
          description: z
            .string()
            .trim()
            .max(AUTOMATION_LIMITS.groupDescriptionMax)
            .nullable()
            .optional(),
          /** The contacts get the link in their 1:1 (default) or are added directly. */
          mode: z.enum(GROUP_JOIN_MODES).optional(),
          welcome: z.string().trim().max(AUTOMATION_LIMITS.messageMax).nullable().optional(),
          /** The group's main responsible: the record's owner (default), a member, the creator. */
          responsible: z.enum(PERSON_TARGETS).optional(),
          memberId: z.string().nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('send_group_message'),
      config: z.object({ text: z.string().trim().max(AUTOMATION_LIMITS.messageMax) }).strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('close_group'),
      config: z
        .object({
          /** Sent before only admins may send; none = no message. */
          message: z.string().trim().max(AUTOMATION_LIMITS.messageMax).nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  // Spec 045: the service cycle, on the run's conversation.
  z
    .object({
      ...StepBase,
      type: z.literal('resolve_conversation'),
      /** The outcome; none fails where the queue requires one. */
      config: z.object({ outcomeId: z.string().nullable().optional() }).strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('transfer_conversation'),
      config: z
        .object({
          target: z.enum(TRANSFER_TARGETS),
          queueId: z.string().nullable().optional(),
          teamId: z.string().nullable().optional(),
          memberId: z.string().nullable().optional(),
          /** A private note left on the thread; may use `{{customer.name}}`... */
          note: z.string().trim().max(AUTOMATION_LIMITS.messageMax).nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('set_conversation_priority'),
      config: z.object({ priority: z.enum(STEP_PRIORITIES) }).strict(),
    })
    .strict(),
  // Spec 047: the service case of the run record.
  z
    .object({
      ...StepBase,
      type: z.literal('create_case'),
      config: z
        .object({
          subject: z.string().trim().max(AUTOMATION_LIMITS.messageMax).nullable().optional(),
          description: z.string().trim().max(AUTOMATION_LIMITS.messageMax).nullable().optional(),
          reasonId: z.string().nullable().optional(),
          queueId: z.string().nullable().optional(),
          assigneeMembershipId: z.string().nullable().optional(),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      ...StepBase,
      type: z.literal('set_case_reason'),
      config: z.object({ reasonId: z.string() }).strict(),
    })
    .strict(),
]);
export type AutomationStep = z.infer<typeof StepSchema>;

export interface AutomationSummary {
  id: string;
  name: string;
  description: string | null;
  kind: AutomationKind;
  status: AutomationStatus;
  trigger: Trigger | null;
  stepCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: PersonRef;
  lastRunAt: string | null;
  runCount: number;
  /** The folder it is filed in (spec 063); null = the root of the list. */
  folderId: string | null;
  /** Runs still in progress (running or waiting), tests apart. */
  activeRunCount: number;
  /** Spec 068: more triggers that also start it (rules only, never a sweep trigger). */
  extraTriggers: Trigger[];
}

/** Spec 070: the automatic drawing, or cards placed by hand (the links stay the graph's). */
export const BUILDER_MODES = ['standard', 'advanced'] as const;
export type BuilderMode = (typeof BUILDER_MODES)[number];

/** Where each card sits in the advanced builder: `trigger`, `trigger:<n>` or a step's id. */
export interface AutomationLayout {
  positions: Record<string, { x: number; y: number }>;
}

export const LAYOUT_LIMITS = { maxPositions: 200, coordinateMax: 100_000 } as const;

const LayoutSchema = z
  .object({
    positions: z
      .record(
        z.string().min(1).max(80),
        z
          .object({
            x: z
              .number()
              .finite()
              .min(-LAYOUT_LIMITS.coordinateMax)
              .max(LAYOUT_LIMITS.coordinateMax),
            y: z
              .number()
              .finite()
              .min(-LAYOUT_LIMITS.coordinateMax)
              .max(LAYOUT_LIMITS.coordinateMax),
          })
          .strict(),
      )
      .refine((positions) => Object.keys(positions).length <= LAYOUT_LIMITS.maxPositions),
  })
  .strict();

export interface Automation extends Omit<AutomationSummary, 'stepCount' | 'activeRunCount'> {
  steps: AutomationStep[];
  builderMode: BuilderMode;
  layout: AutomationLayout | null;
  /** Cadence-only: stop the sequence when the contact replies. */
  stopOnReply: boolean;
  /** Cadence-only: stop when a linked deal changes stage. */
  stopOnDealStageChange: boolean;
  /** At most one run per record in this many days; null = no limit. */
  frequencyDays: number | null;
}

/** The record a run acts on. */
export interface RunSubject {
  type: 'customer' | 'deal' | 'conversation' | 'task';
  id: string;
  label: string | null;
}

/** One step of a run, in the order it ran. */
export interface RunStepLog {
  stepId: string;
  name: string;
  type: string;
  status: 'done' | 'skipped' | 'failed' | 'waiting';
  detail: string | null;
  at: string;
  /** A step that sends a message: what became of it (the provider may refuse it later). */
  delivery?: {
    status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
    failureCode: string | null;
  };
}

export interface AutomationRun {
  id: string;
  automationId: string;
  status: RunStatus;
  /** "Testar": walked without side effects. */
  test: boolean;
  subject: RunSubject | null;
  startedAt: string;
  /** Null while the run is in progress. */
  finishedAt: string | null;
  summary: string;
  error: string | null;
  steps: RunStepLog[];
  /** Spec 069: the trigger that started it (`manual` for an enrollment). */
  triggerType: string;
  /** The step it runs next or waits on; null when finished. */
  currentStepId: string | null;
  /** When a waiting run resumes. */
  resumeAt: string | null;
}

/** Filters of `GET /automations/:id/runs` (spec 069); every one optional. */
export interface AutomationRunsQuery {
  status?: RunStatus;
  /** `YYYY-MM-DD` in the organization's time zone, by when the run started. */
  from?: string;
  to?: string;
  /** Text in the name of the run's record. */
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface AutomationRunsPage {
  items: AutomationRun[];
  nextCursor: string | null;
}

/** At most one run per record in this many days; null = no limit. */
const frequencySchema = z
  .number()
  .int()
  .min(AUTOMATION_LIMITS.frequencyDaysMin)
  .max(AUTOMATION_LIMITS.frequencyDaysMax)
  .nullable();

export const CreateAutomationSchema = z
  .object({
    name: nameSchema,
    kind: z.enum(AUTOMATION_KINDS),
    description: z.string().trim().max(AUTOMATION_LIMITS.descriptionMax).nullable().optional(),
    trigger: TriggerSchema.nullable().optional(),
    steps: z.array(StepSchema).max(AUTOMATION_LIMITS.maxSteps).optional(),
    stopOnReply: z.boolean().optional(),
    stopOnDealStageChange: z.boolean().optional(),
    frequencyDays: frequencySchema.optional(),
    folderId: idSchema.nullable().optional(),
    extraTriggers: z.array(TriggerSchema).max(AUTOMATION_LIMITS.maxExtraTriggers).optional(),
    builderMode: z.enum(BUILDER_MODES).optional(),
    layout: LayoutSchema.nullable().optional(),
  })
  .strict();
export type CreateAutomationInput = z.infer<typeof CreateAutomationSchema>;

export const UpdateAutomationSchema = z
  .object({
    name: nameSchema.optional(),
    description: z.string().trim().max(AUTOMATION_LIMITS.descriptionMax).nullable().optional(),
    trigger: TriggerSchema.nullable().optional(),
    steps: z.array(StepSchema).max(AUTOMATION_LIMITS.maxSteps).optional(),
    stopOnReply: z.boolean().optional(),
    stopOnDealStageChange: z.boolean().optional(),
    frequencyDays: frequencySchema.optional(),
    folderId: idSchema.nullable().optional(),
    extraTriggers: z.array(TriggerSchema).max(AUTOMATION_LIMITS.maxExtraTriggers).optional(),
    builderMode: z.enum(BUILDER_MODES).optional(),
    layout: LayoutSchema.nullable().optional(),
  })
  .strict();
export type UpdateAutomationInput = z.infer<typeof UpdateAutomationSchema>;

export const AutomationListQuerySchema = z
  .object({
    kind: z.enum(AUTOMATION_KINDS).optional(),
    status: z.enum(AUTOMATION_STATUSES).optional(),
    q: z.string().trim().max(120).optional(),
    /** A folder's id, or `root` for the automations outside every folder; none = all. */
    folderId: z.string().min(1).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
  })
  .strict();
export type AutomationListQuery = z.infer<typeof AutomationListQuerySchema>;

export interface AutomationListPage {
  items: AutomationSummary[];
  nextCursor: string | null;
}

/* ------------------------------------------------------------------ folders (spec 063) */

/** A folder of the automations list (one level). */
export interface AutomationFolder {
  id: string;
  name: string;
  /** Live (not archived) automations filed in it. */
  automationCount: number;
  createdAt: string;
  updatedAt: string;
}

export const AutomationFolderInputSchema = z
  .object({ name: z.string().trim().min(1).max(AUTOMATION_LIMITS.folderNameMax) })
  .strict();
export type AutomationFolderInput = z.infer<typeof AutomationFolderInputSchema>;

/** `POST /automations/:id/duplicate`: the copy's name (the UI words it). */
export const DuplicateAutomationSchema = z.object({ name: nameSchema }).strict();
export type DuplicateAutomationInput = z.infer<typeof DuplicateAutomationSchema>;

/** Whether a step type is allowed on this kind of automation. */
export function stepAllowedOn(kind: AutomationKind, type: StepType): boolean {
  if (kind === 'cadence') {
    return (CADENCE_STEP_TYPES as readonly string[]).includes(type);
  }
  return true;
}

/**
 * Validates the graph before activate: every nextStepId exists, no cycles, condition has
 * exactly two next steps (or zero while still drafting), other steps have at most one.
 */
export function validateAutomationGraph(
  trigger: Trigger | null,
  steps: readonly AutomationStep[],
): string | null {
  if (!trigger) return 'trigger_required';
  if (trigger.type === 'date.offset' && trigger.anchor === 'customer.field' && !trigger.fieldKey) {
    return 'date_field_required';
  }
  if (trigger.type === 'customer.tag_added' && trigger.tags.length === 0) return 'tag_required';
  if (steps.length === 0) return 'steps_required';
  if (steps.length > AUTOMATION_LIMITS.maxSteps) return 'too_many_steps';

  const byId = new Map(steps.map((step) => [step.id, step]));
  if (byId.size !== steps.length) return 'duplicate_step_id';

  for (const step of steps) {
    for (const nextId of step.nextStepIds) {
      if (nextId === '' && isBranchStep(step.type)) continue;
      if (!byId.has(nextId)) return 'unknown_next_step';
    }
    if (isBranchStep(step.type)) {
      if (step.nextStepIds.length !== 2) return 'condition_needs_branches';
    } else if (step.nextStepIds.length > 1) {
      return 'linear_step_branch';
    }
  }

  // Cycle detection from every root (steps never pointed at).
  const pointed = new Set(steps.flatMap((step) => step.nextStepIds).filter(Boolean));
  const roots = steps.filter((step) => !pointed.has(step.id));
  if (roots.length === 0) return 'cycle';

  const visiting = new Set<string>();
  const visited = new Set<string>();
  const walk = (id: string): boolean => {
    if (visiting.has(id)) return true;
    if (visited.has(id)) return false;
    visiting.add(id);
    const step = byId.get(id);
    if (!step) return false;
    for (const nextId of step.nextStepIds) {
      if (nextId && walk(nextId)) return true;
    }
    visiting.delete(id);
    visited.add(id);
    return false;
  };
  for (const root of roots) {
    if (walk(root.id)) return 'cycle';
  }
  if (steps.some(stepIncomplete)) return 'incomplete_step';
  return readsReplyWithoutWait(roots, byId) ? 'reply_without_wait' : null;
}

/** Spec 072: a condition on the reply is only meaningful after a `wait_for_reply` on its path. */
function readsReplyWithoutWait(
  roots: readonly AutomationStep[],
  byId: ReadonlyMap<string, AutomationStep>,
): boolean {
  const visit = (step: AutomationStep, waited: boolean): boolean => {
    if (
      step.type === 'condition' &&
      !waited &&
      step.config.conditions.some((condition) => condition.field === 'reply.text')
    ) {
      return true;
    }
    const after = waited || step.type === 'wait_for_reply';
    return step.nextStepIds.some((nextId) => {
      const next = byId.get(nextId);
      return next ? visit(next, after) : false;
    });
  };
  return roots.some((root) => visit(root, false));
}

/** A short English key describing the trigger for lists (the UI translates it). */
export function triggerSummaryKey(trigger: Trigger | null): string {
  if (!trigger) return 'none';
  return trigger.type;
}

/**
 * Whether a step still lacks what it needs to run (a draft may be saved like this; activating
 * refuses it). The lost reason of a lost stage is checked when the step runs.
 */
export function stepIncomplete(step: AutomationStep): boolean {
  switch (step.type) {
    case 'condition':
      return step.config.conditions.some(conditionIncomplete);
    case 'send_message':
      return step.config.text.trim() === '';
    case 'send_email':
      return (
        step.config.channelConnectionId === '' ||
        step.config.subject.trim() === '' ||
        step.config.text.trim() === ''
      );
    case 'update_field':
      return step.config.key.trim() === '';
    case 'start_cadence':
      return step.config.automationId === '';
    case 'send_template':
      return step.config.templateKey.trim() === '';
    case 'move_deal_stage':
      return step.config.pipelineId === '' || step.config.stageId === '';
    case 'find_deal':
      return step.config.pipelineId === '';
    case 'create_deal':
      return (
        step.config.pipelineId === '' ||
        step.config.stageId === '' ||
        (step.config.owner === 'member' && !step.config.memberId)
      );
    case 'webhook':
      return !step.config.url.startsWith('https://');
    case 'integration_action':
      return step.config.integrationId === '' || step.config.actionKey === '';
    case 'assign':
      return (
        (step.config.assignee === 'member' && !step.config.memberId) ||
        (step.config.assignee === 'rotation' && !step.config.queueId)
      );
    case 'create_task':
      return step.config.assignee === 'member' && !step.config.memberId;
    case 'notify':
      return step.config.recipient === 'member' && !step.config.memberId;
    case 'create_group':
      return (
        step.config.channelConnectionId === '' ||
        step.config.subject.trim() === '' ||
        (step.config.responsible === 'member' && !step.config.memberId)
      );
    case 'send_group_message':
      return step.config.text.trim() === '';
    case 'set_case_reason':
      return step.config.reasonId === '';
    case 'transfer_conversation':
      return (
        (step.config.target === 'queue' && !step.config.queueId) ||
        (step.config.target === 'team' && !step.config.teamId) ||
        (step.config.target === 'member' && !step.config.memberId)
      );
    default:
      return false;
  }
}

/**
 * A condition activating refuses: an operator its field does not take, a custom field without
 * its key, or a comparison without a value.
 */
export function conditionIncomplete(condition: Condition): boolean {
  if (!CONDITION_OPERATORS_BY_FIELD[condition.field].includes(condition.operator)) return true;
  if (CUSTOM_FIELD_CONDITIONS.includes(condition.field) && !condition.key?.trim()) return true;
  return !VALUELESS_OPERATORS.includes(condition.operator) && condition.value.trim() === '';
}

/* ------------------------------------------------------------------ a record's automations */

/** A run seen from the deal or customer it is about (spec 012 §E). */
export interface RecordRun extends AutomationRun {
  automation: { id: string; name: string; kind: AutomationKind };
  /** The step it runs next or waits on; null when finished. */
  currentStep: string | null;
  /** When a waiting run resumes. */
  resumeAt: string | null;
}

/** The automations card of a deal or a customer. */
export interface RecordAutomations {
  /** Active cadences the record can be enrolled in. */
  cadences: { id: string; name: string }[];
  runs: RecordRun[];
}

/** The record the card is about: a deal or a customer. */
export type RecordRef = { dealId: string } | { customerId: string };

/**
 * What activating refuses about the extra triggers (spec 068): a cadence has none, and an
 * extra trigger is never one a sweep starts. Null when they are fine.
 */
export function extraTriggersProblem(
  kind: AutomationKind,
  extraTriggers: readonly Trigger[],
): 'extra_triggers_rule_only' | 'extra_trigger_not_allowed' | null {
  if (extraTriggers.length === 0) return null;
  if (kind !== 'rule') return 'extra_triggers_rule_only';
  return extraTriggers.some((trigger) => SWEEP_TRIGGER_TYPES.includes(trigger.type))
    ? 'extra_trigger_not_allowed'
    : null;
}

/* ------------------------------------------------------------ versions (spec 070) */

export const VERSION_REASONS = ['create', 'save', 'activate', 'restore'] as const;
export type VersionReason = (typeof VERSION_REASONS)[number];

/** A saved state of an automation, newest first in its list. */
export interface AutomationVersionSummary {
  id: string;
  number: number;
  reason: VersionReason;
  createdAt: string;
  createdBy: PersonRef | null;
  stepCount: number;
  triggerType: string | null;
}

/** What a version holds: the automation as it was saved. */
export interface AutomationSnapshot {
  name: string;
  description: string | null;
  trigger: Trigger | null;
  extraTriggers: Trigger[];
  steps: AutomationStep[];
  stopOnReply: boolean;
  stopOnDealStageChange: boolean;
  frequencyDays: number | null;
  builderMode: BuilderMode;
  layout: AutomationLayout | null;
}

export interface AutomationVersion extends AutomationVersionSummary {
  snapshot: AutomationSnapshot;
}

/* ------------------------------------------------------------ statistics (spec 070) */

/** Why an event did not start the automation (the filter that did not match, or the chain). */
export interface TriggerCheck {
  id: string;
  triggerIndex: number;
  triggerType: string;
  matched: boolean;
  reason: string | null;
  subject: { type: string; id: string; label: string | null } | null;
  at: string;
}

export interface AutomationStats {
  /** The days counted (the last 30 when none was asked). */
  period: { from: string; to: string };
  triggers: {
    index: number;
    type: string;
    attempted: number;
    matched: number;
    unmatched: number;
  }[];
  steps: { stepId: string; done: number; skipped: number; failed: number; waiting: number }[];
  runs: { total: number; succeeded: number; failed: number; inProgress: number };
}

export interface StatsPeriod {
  from?: string;
  to?: string;
}

export interface TriggerChecksQuery extends StatsPeriod {
  index?: number;
  matched?: boolean;
  q?: string;
  cursor?: string;
  limit?: number;
}
